import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import type { Badge, MembershipBadgeType } from '@freelancer/ui/badge';
import {
  BadgeSize,
  BadgeType,
  CorporateBadgeType,
  PreferredFreelancerBadgeType,
  RecruiterBadgeType,
  RisingStarBadgeType,
  StaffBadgeType,
  UserBadgeType,
  VerifiedBadgeType,
} from '@freelancer/ui/badge';
import { HeadingColor, HeadingWeight } from '@freelancer/ui/heading';
import { LinkColor, QueryParams } from '@freelancer/ui/link';
import { textVariants } from '@freelancer/ui/text';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import {
  UsernameBadges,
  UsernameColor,
  UsernameMembershipPackage,
  UsernameSize,
} from './username.types';

@Component({
  standalone: false,
  selector: 'fl-username',
  template: `
    @if (!isLink) {
      @if (!isHeading) {
        <div
          class="Username-row"
          [attr.data-size]="size"
          [attr.data-size-tablet]="sizeTablet"
          [attr.data-size-desktop]="sizeDesktop"
          [attr.data-truncate]="truncateText"
        >
          @if (country) {
            <fl-flag
              class="Username-flag mr-xxsmall"
              [attr.data-size]="size"
              [attr.data-size-tablet]="sizeTablet"
              [attr.data-size-desktop]="sizeDesktop"
              [countryCode]="country"
            ></fl-flag>
          }
          @if (displayName) {
            <p
              class="Username-displayName {{ displayNameClass }} {{
                compact ? 'mr-none' : 'mr-xxxsmall'
              }} {{ compactTablet ? 'tablet:mr-none' : 'tablet:mr-xxxsmall' }}"
              [attr.data-truncate]="truncateText"
              [class.desktop:hide]="hideDisplayName"
              [class.mobile:hide]="hideDisplayNameOnMobile || hideDisplayName"
              [class.tablet:hide]="hideDisplayName"
            >
              {{ displayName }}
            </p>
          }
        </div>
        <div
          class="Username-row"
          [attr.data-truncate]="truncateText"
          [attr.data-size]="size"
          [attr.data-size-tablet]="sizeTablet"
          [attr.data-size-desktop]="sizeDesktop"
        >
          @if (showUsernameTag) {
            <p
              class="Username-userId {{ usernameClass }}"
              [attr.data-truncate]="truncateText"
              [attr.data-size]="size"
              [attr.data-size-tablet]="sizeTablet"
              [attr.data-size-desktop]="sizeDesktop"
            >
              <ng-container *ngTemplateOutlet="userhandle"></ng-container>
            </p>
          }
          <ng-container
            *ngTemplateOutlet="userBadges; context: { badges: badges }"
          ></ng-container>
        </div>
      }
      @if (isHeading) {
        <div
          class="Username-row"
          [attr.data-size]="size"
          [attr.data-size-tablet]="sizeTablet"
          [attr.data-size-desktop]="sizeDesktop"
        >
          @if (country) {
            <fl-flag
              class="Username-flag mr-xxsmall"
              [attr.data-size]="size"
              [attr.data-size-tablet]="sizeTablet"
              [attr.data-size-desktop]="sizeDesktop"
              [countryCode]="country"
            ></fl-flag>
          }
          @if (displayName) {
            <h3
              [class]="displayNameHeaderClass"
              [attr.data-truncate]="truncateText"
              [attr.data-size-tablet]="sizeTablet"
              [attr.data-size-desktop]="sizeDesktop"
              [class.mr-none]="compact"
              [class.mr-xxxsmall]="!compact"
              [class.tablet:mr-none]="
                displayNameHeadingMarginRightTablet === 'none'
              "
              [class.tablet:mr-xsmall]="
                displayNameHeadingMarginRightTablet === 'xsmall'
              "
              [class.tablet:mr-xxxsmall]="
                displayNameHeadingMarginRightTablet === 'xxxsmall'
              "
              [class.desktop:hide]="hideDisplayName"
              [class.mobile:hide]="hideDisplayNameOnMobile || hideDisplayName"
              [class.tablet:hide]="hideDisplayName"
            >
              {{ displayName }}
            </h3>
          }
        </div>
        <div
          class="Username-row"
          [attr.data-size]="size"
          [attr.data-size-tablet]="sizeTablet"
          [attr.data-size-desktop]="sizeDesktop"
        >
          @if (showUsernameTag) {
            <h3
              [class]="usernameHeadingClass"
              [attr.data-compact]="compact"
              [attr.data-compact-tablet]="compactTablet"
              [attr.data-truncate]="truncateText"
              [attr.data-size]="size"
              [attr.data-size-tablet]="sizeTablet"
              [attr.data-size-desktop]="sizeDesktop"
            >
              <ng-container *ngTemplateOutlet="userhandle"></ng-container>
            </h3>
          }
          <ng-container
            *ngTemplateOutlet="userBadges; context: { badges: badges }"
          ></ng-container>
        </div>
      }
    }
    @if (isLink) {
      <div
        class="Username-row"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-truncate]="truncateText"
      >
        @if (country) {
          <fl-flag
            class="Username-flag mr-xxsmall"
            [attr.data-size]="size"
            [attr.data-size-tablet]="sizeTablet"
            [attr.data-size-desktop]="sizeDesktop"
            [countryCode]="country"
          ></fl-flag>
        }
        @if (displayName) {
          <span
            [class]="linkDisplayNameClass"
            [attr.data-truncate]="truncateText"
            [class.mr-none]="compact"
            [class.mr-xxxsmall]="!compact"
            [class.tablet:mr-none]="compactTablet"
            [class.tablet:mr-xxxsmall]="!compactTablet"
            [class.mobile:hide]="hideDisplayNameOnMobile"
          >
            {{ displayName }}
          </span>
        }
      </div>
      <div
        class="Username-row"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-truncate]="truncateText"
      >
        <span
          [class]="linkUsernameClass"
          [attr.data-truncate]="truncateText"
        >
          @if (showUsernameTag) {
            <fl-link
              [attr.data-size]="size"
              [attr.data-size-tablet]="sizeTablet"
              [attr.data-size-desktop]="sizeDesktop"
              [color]="LinkColor.INHERIT"
              [isAbsoluteUrl]="true"
              [link]="link"
              [newTab]="newTab"
              [size]="userHandleSize"
              [sizeTablet]="userHandleSizeTablet"
              [sizeDesktop]="userHandleSizeDesktop"
              [sizeDesktopXXLarge]="userHandleSizeDesktopXXLarge"
              [queryParams]="queryParams"
            >
              <ng-container *ngTemplateOutlet="userhandle"></ng-container>
            </fl-link>
          }
        </span>
        <ng-container
          *ngTemplateOutlet="userBadges; context: { badges: badges }"
        ></ng-container>
      </div>
    }

    <ng-template #userhandle>&#64;{{ username }}</ng-template>

    <ng-template
      #userBadges
      let-badges="badges"
    >
      @if (badges || membershipPackage) {
        <div
          class="Username-badges"
          [attr.data-size]="size"
          [attr.data-size-tablet]="sizeTablet"
          [attr.data-size-desktop]="sizeDesktop"
        >
          @if (
            membershipPackage &&
            membershipPackage.name !== 'free' &&
            membershipPackage.badgeType
          ) {
            <fl-badge
              class="Username-badge"
              matTooltip="{{ membershipPackage.name | titlecase }} Membership"
              i18n-matTooltip="UserMembershipBadgeTooltipMessage"
              [ariaLabel]="
                (membershipPackage.name | titlecase) + ' Membership badge'
              "
              [badge]="getMembershipBadge(membershipPackage.badgeType)"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
          @if (badges?.verified) {
            <fl-badge
              class="Username-badge"
              matTooltip="Verified Freelancer"
              i18n-matTooltip="Verified Freelancer badge tooltip message"
              aria-label="Verified by Freelancer badge"
              i18n-aria-label="Verified by Freelancer badge"
              [badge]="verifiedBadge"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
          @if (badges?.preferred_freelancer) {
            <fl-badge
              class="Username-badge"
              matTooltip="Preferred Freelancer"
              i18n-matTooltip="Preferred Freelancer badge tooltip message"
              aria-label="Preferred Freelancer badge"
              i18n-aria-label="Preferred Freelancer badge"
              [badge]="preferredFreelancerBadge"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
          @if (badges?.rising_star) {
            <fl-badge
              class="Username-badge"
              matTooltip="Rising Star"
              i18n-matTooltip="Rising Star badge tooltip message"
              aria-label="Rising Star badge"
              i18n-aria-label="Rising Star badge"
              [badge]="risingStarBadge"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
          @if (badges?.corporate) {
            <fl-badge
              class="Username-badge"
              matTooltip="Corporate Freelancer"
              i18n-matTooltip="Corporate Freelancer badge tooltip message"
              aria-label="Corporate Freelancer badge"
              i18n-aria-label="Corporate Freelancer badge"
              [badge]="corporateBadge"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
          @if (badges?.shareholder) {
            <fl-badge
              class="Username-badge"
              matTooltip="Foundation Shareholder"
              i18n-matTooltip="Foundation Shareholder badge tooltip message"
              aria-label="Foundation Shareholder badge"
              i18n-aria-label="Foundation Shareholder badge"
              [badge]="shareholderBadge"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
          @if (badges?.do_gooder) {
            <fl-badge
              class="Username-badge"
              aria-label="Do Gooder badge"
              i18n-aria-label="Do Gooder badge"
              matTooltip="Do Gooder"
              i18n-matTooltip="Tooltip for a Do Gooder badge"
              [badge]="doGooderBadge"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
          @if (badges?.freelancer_staff) {
            <fl-badge
              class="Username-badge"
              matTooltip="Freelancer Staff"
              i18n-matTooltip="Freelancer Staff badge tooltip message"
              aria-label="Freelancer Staff badge"
              i18n-aria-label="Freelancer Staff badge"
              [badge]="freelancerStaffBadge"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
          @if (badges?.freelancer_recruiter) {
            <fl-badge
              class="Username-badge"
              matTooltip="Freelancer Recruiter"
              i18n-matTooltip="Freelancer Recruiter badge tooltip message"
              aria-label="Freelancer Recruiter badge"
              i18n-aria-label="Freelancer Recruiter badge"
              [badge]="freelancerRecruiterBadge"
              [size]="badgeSize"
              [sizeTablet]="badgeSizeTablet"
              [sizeDesktop]="badgeSizeDesktop"
              [sizeDesktopXXLarge]="badgeSizeDesktopXXLarge"
            ></fl-badge>
          }
        </div>
      }
    </ng-template>
  `,
  styleUrls: ['./username.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsernameComponent implements OnChanges, OnInit {
  LinkColor = LinkColor;

  badgeSize = BadgeSize.SMALL;
  badgeSizeTablet?: BadgeSize | undefined;
  badgeSizeDesktop?: BadgeSize | undefined;
  badgeSizeDesktopXXLarge?: BadgeSize | undefined;
  displayNameFontColor: FontColor;
  displayNameHeadingColor: HeadingColor;
  usernameFontColor: FontColor;
  usernameHeadingColor: HeadingColor;

  userHandleSize: TextSize;
  userHandleSizeTablet?: TextSize | undefined;
  userHandleSizeDesktop?: TextSize | undefined;
  // TODO: T298322 This should ONLY used for the username in the profile summary and will be cleaned up in T298322
  userHandleSizeDesktopXXLarge?: TextSize | undefined;
  usernameSize: TextSize;
  usernameSizeTablet?: TextSize | undefined;
  usernameSizeDesktop?: TextSize | undefined;
  // TODO: T298322 This should ONLY used for the username in the profile summary and will be cleaned up in T298322
  usernameSizeDesktopXXLarge?: TextSize | undefined;
  isHeading = false;

  get verifiedBadge(): Badge {
    return {
      type: BadgeType.VERIFIED,
      badge: VerifiedBadgeType.VERIFIED,
    };
  }

  preferredFreelancerBadge: Badge = {
    type: BadgeType.PREFERRED_FREELANCER,
    badge: PreferredFreelancerBadgeType.PREFERRED_FREELANCER,
  };

  corporateBadge: Badge = {
    type: BadgeType.CORPORATE,
    badge: CorporateBadgeType.CORPORATE,
  };

  shareholderBadge: Badge = {
    type: BadgeType.USER,
    badge: UserBadgeType.FOUNDATION_SHAREHOLDER,
  };

  risingStarBadge: Badge = {
    type: BadgeType.RISING_STAR,
    badge: RisingStarBadgeType.RISING_STAR,
  };

  doGooderBadge: Badge = {
    type: BadgeType.USER,
    badge: UserBadgeType.DO_GOODER,
  };

  freelancerStaffBadge: Badge = {
    type: BadgeType.STAFF,
    badge: StaffBadgeType.FREELANCER_STAFF,
  };

  freelancerRecruiterBadge: Badge = {
    type: BadgeType.RECRUITER,
    badge: RecruiterBadgeType.FREELANCER_RECRUITER,
  };

  @Input() color = UsernameColor.FOREGROUND;
  @Input() displayName?: string;
  /** Hide display name for all screen sizes */
  @Input() hideDisplayName = false;
  /** Hide display name for mobile only */
  @Input() hideDisplayNameOnMobile = false;
  @Input() username: string;
  @Input() isBold = true;
  @Input() usernameWeight?: FontWeight;

  @HostBinding('attr.data-user-id-weight')
  get userIdWeightAttr(): FontWeight | undefined {
    return this.usernameWeight;
  }

  /** We don't advise the usage of country flags with new implementations of
  the username component. Please seek approval from management for permission
  to implement a country flag within the username component. */
  @Input() country?: string;
  @Input() queryParams?: QueryParams;
  @Input() link?: string;
  get isLink(): boolean {
    return this.link !== undefined ? this.link.length > 0 : false;
  }
  @Input() newTab?: boolean;
  @Input() truncateText?: boolean;

  @Input() size = UsernameSize.MID;
  @Input() sizeTablet?: UsernameSize;
  @Input() sizeDesktop?: UsernameSize;
  @Input() sizeDesktopXXLarge?: UsernameSize;

  @HostBinding('attr.data-compact')
  @Input()
  compact = false;

  @HostBinding('attr.data-compact-tablet')
  @Input()
  compactTablet?: boolean;

  /** We recommend to not use this option, username should always be shown. */
  @Input() showUsernameTag? = true;

  @Input() badges?: UsernameBadges;
  @Input() membershipPackage?: UsernameMembershipPackage;

  /** Re-enable pointer events on badges when an ancestor uses pointer-events:none. */
  @HostBinding('attr.data-interactive-badges')
  @Input()
  interactiveBadges = false;

  displayNameClass: string;
  displayNameHeaderClass: string;
  usernameHeadingClass: string;
  usernameClass: string;
  linkDisplayNameClass: string;
  linkUsernameClass: string;
  displayNameHeadingMarginRightTablet: 'none' | 'xsmall' | 'xxxsmall';

  ngOnInit(): void {
    this.initializeUsername();
    this.initializeDisplayNameHeadingMarginRightTablet();
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('color' in changes) {
      this.setUsernameColor();
    }

    if ('size' in changes) {
      this.getUsernameSize();
      this.getBadgeSize();
    }

    this.computeClasses();
  }

  initializeUsername(): void {
    this.setUsernameColor();
    this.getUsernameSize();
  }

  private computeClasses(): void {
    this.linkUsernameClass = textVariants({
      color: this.usernameFontColor,
      weight: this.getUserIdFontWeight(),
      class: 'Username-userId',
    });

    this.linkDisplayNameClass = textVariants({
      color: this.displayNameFontColor,
      size: this.usernameSize,
      sizeTablet: this.usernameSizeTablet,
      sizeDesktop: this.usernameSizeDesktop,
      sizeDesktopXXL: this.usernameSizeDesktopXXLarge,
      weight: FontWeight.INHERIT,
      class: 'Username-displayName',
    });

    this.displayNameClass = textVariants({
      color: this.displayNameFontColor,
      size: this.usernameSize,
      sizeTablet: this.usernameSizeTablet,
      sizeDesktop: this.usernameSizeDesktop,
      sizeDesktopXXL: this.usernameSizeDesktopXXLarge,
    });

    this.usernameClass = textVariants({
      color: this.usernameFontColor,
      size: this.userHandleSize,
      sizeTablet: this.userHandleSizeTablet,
      sizeDesktop: this.userHandleSizeDesktop,
      sizeDesktopXXL: this.userHandleSizeDesktopXXLarge,
      weight: this.getUserIdFontWeight(),
    });

    this.displayNameHeaderClass = textVariants({
      color: this.displayNameFontColor,
      size: this.usernameSize,
      sizeTablet: this.usernameSizeTablet,
      sizeDesktop: this.usernameSizeDesktop,
      sizeDesktopXXL: this.usernameSizeDesktopXXLarge,
      weight: HeadingWeight.INHERIT,
      class: 'Username-displayName',
    });

    this.usernameHeadingClass = textVariants({
      color: this.usernameHeadingColor,
      size: this.userHandleSize,
      sizeTablet: this.userHandleSizeTablet,
      sizeDesktop: this.userHandleSizeDesktop,
      sizeDesktopXXL: this.userHandleSizeDesktopXXLarge,
      weight: this.getUserIdHeadingWeight(),
      class: 'Username-userId Username-userId-heading',
    });
  }

  private getUserIdFontWeight(): FontWeight {
    if (this.usernameWeight) {
      return this.usernameWeight;
    }

    return this.displayName || !this.isBold
      ? FontWeight.INHERIT
      : FontWeight.BOLD;
  }

  private getUserIdHeadingWeight(): HeadingWeight {
    if (this.usernameWeight) {
      switch (this.usernameWeight) {
        case FontWeight.BLACK:
          return HeadingWeight.BLACK;
        case FontWeight.BOLD:
          return HeadingWeight.BOLD;
        case FontWeight.MEDIUM:
          return HeadingWeight.MEDIUM;
        case FontWeight.NORMAL:
          return HeadingWeight.NORMAL;
        case FontWeight.LIGHT:
          return HeadingWeight.LIGHT;
        default:
          return HeadingWeight.INHERIT;
      }
    }

    return this.displayName || !this.isBold
      ? HeadingWeight.INHERIT
      : HeadingWeight.BOLD;
  }

  setUsernameColor(): void {
    switch (this.color) {
      case UsernameColor.FOREGROUND:
        this.displayNameFontColor = FontColor.FOREGROUND;
        this.displayNameHeadingColor = HeadingColor.FOREGROUND;
        this.usernameFontColor = FontColor.FOREGROUND;
        this.usernameHeadingColor = HeadingColor.FOREGROUND;
        break;
      case UsernameColor.LIGHT:
        this.displayNameFontColor = FontColor.LIGHT;
        this.displayNameHeadingColor = HeadingColor.LIGHT;
        this.usernameFontColor = FontColor.LIGHT;
        this.usernameHeadingColor = HeadingColor.LIGHT;
        break;
      case UsernameColor.INHERIT:
        this.displayNameFontColor = FontColor.INHERIT;
        this.displayNameHeadingColor = HeadingColor.INHERIT;
        this.usernameFontColor = FontColor.INHERIT;
        this.usernameHeadingColor = HeadingColor.INHERIT;
        break;
      default:
        this.displayNameFontColor = FontColor.DARK;
        this.displayNameHeadingColor = HeadingColor.DARK;
        this.usernameFontColor = FontColor.DARK;
        this.usernameHeadingColor = HeadingColor.DARK;
    }
  }

  generateBadgeSize(size: UsernameSize | undefined): BadgeSize | undefined {
    switch (size) {
      case UsernameSize.PROFILE_SUMMARY_MOBILE:
      case UsernameSize.XXLARGE:
      case UsernameSize.XLARGE:
        return BadgeSize.MID;
      case UsernameSize.LARGE:
      case UsernameSize.MID:
      case UsernameSize.SMALL:
        return BadgeSize.SMALL;
      case UsernameSize.PROFILE_SUMMARY:
        return BadgeSize.PROFILE;
      default:
        return undefined;
    }
  }

  getBadgeSize(): void {
    // Set default size to BadgeSize.SMALL
    this.badgeSize = this.generateBadgeSize(this.size) || BadgeSize.SMALL;
    this.badgeSizeTablet = this.generateBadgeSize(this.sizeTablet);
    this.badgeSizeDesktop = this.generateBadgeSize(this.sizeDesktop);
    this.badgeSizeDesktopXXLarge = this.generateBadgeSize(
      this.sizeDesktopXXLarge,
    );
  }

  getMembershipBadge(badge: MembershipBadgeType): Badge {
    return {
      type: BadgeType.MEMBERSHIP,
      badge,
    };
  }

  generateUsernameSize(size: UsernameSize | undefined): TextSize | undefined {
    switch (size) {
      case UsernameSize.XXLARGE:
        return TextSize.BODY_24;
      case UsernameSize.XLARGE:
        return TextSize.BODY_20;
      case UsernameSize.LARGE:
        return TextSize.BODY_16;
      case UsernameSize.MID:
        return TextSize.BODY_16;
      case UsernameSize.SMALL:
        return TextSize.BODY_16;
      case UsernameSize.PROFILE_SUMMARY_MOBILE:
        return TextSize.TITLE_32;
      case UsernameSize.PROFILE_SUMMARY:
        return TextSize.TITLE_50;
      default:
        return undefined;
    }
  }

  isUsernameSizeHeading(size: UsernameSize): boolean {
    // Heading is true only for UsernameSize.PROFILE_SUMMARY, Username.PROFILE_SUMMARY_MOBILE, Username.XXLARGE, and Username.XLARGE
    return [
      UsernameSize.PROFILE_SUMMARY,
      UsernameSize.PROFILE_SUMMARY_MOBILE,
      UsernameSize.XXLARGE,
      UsernameSize.XLARGE,
    ].includes(size);
  }

  getUsernameSize(): void {
    // Set default size to TextSize.BODY_16
    this.usernameSize =
      this.generateUsernameSize(this.size) || TextSize.BODY_16;
    this.usernameSizeTablet = this.generateUsernameSize(this.sizeTablet);
    this.usernameSizeDesktop = this.generateUsernameSize(this.sizeDesktop);
    this.usernameSizeDesktopXXLarge =
      this.sizeTablet === UsernameSize.PROFILE_SUMMARY
        ? TextSize.TITLE_60
        : this.generateUsernameSize(this.sizeDesktopXXLarge);
    this.isHeading = this.isUsernameSizeHeading(this.size);
    this.userHandleSize = this.usernameSize;
    if (this.size === UsernameSize.PROFILE_SUMMARY) {
      this.userHandleSize = TextSize.TITLE_40;
    } else if (this.size === UsernameSize.PROFILE_SUMMARY_MOBILE) {
      this.userHandleSize = TextSize.BODY_24;
    }
    this.userHandleSizeTablet = this.usernameSizeTablet;
    if (this.sizeTablet === UsernameSize.PROFILE_SUMMARY) {
      this.userHandleSizeTablet = TextSize.TITLE_40;
    } else if (this.sizeTablet === UsernameSize.PROFILE_SUMMARY_MOBILE) {
      this.userHandleSizeTablet = TextSize.BODY_24;
    }
    this.userHandleSizeDesktop = this.usernameSizeDesktop;
    if (this.sizeDesktop === UsernameSize.PROFILE_SUMMARY) {
      this.userHandleSizeDesktop = TextSize.TITLE_40;
    } else if (this.sizeDesktop === UsernameSize.PROFILE_SUMMARY_MOBILE) {
      this.userHandleSizeDesktop = TextSize.BODY_24;
    }
    this.userHandleSizeDesktopXXLarge =
      this.sizeTablet === UsernameSize.PROFILE_SUMMARY
        ? TextSize.TITLE_50
        : this.usernameSizeDesktopXXLarge;
  }

  initializeDisplayNameHeadingMarginRightTablet(): void {
    if (this.compactTablet) {
      this.displayNameHeadingMarginRightTablet = 'none';
    } else if (this.size === UsernameSize.PROFILE_SUMMARY_MOBILE) {
      this.displayNameHeadingMarginRightTablet = 'xsmall';
    } else {
      this.displayNameHeadingMarginRightTablet = 'xxxsmall';
    }
  }
}
